import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section, run } from "../sales/dmsUi";
import { AppDropdown, EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Fleet Sales: agreements fixing a corporate customer's prices per variant for a period (approved by
// a sales manager), and fleet orders placed against them - many vehicles, delivered in batches,
// invoiced together from the order.

function AgreementEditor({ onHide, onDone }) {
  const fin = useFinOptions(["locations"]);
  const opts = useDmsOptions(["fleet-accounts", "variants"]);
  const [h, setH] = useState({ fleet_account_id: null, location_id: null, valid_from: todayYmd(), valid_to: "", remarks: "" });
  const [lines, setLines] = useState([{ variant_id: null, price: "", list_price: "", max_units: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const save = async () => {
    setBusy(true);
    const res = await run(() =>
      dmsService.saveFleetAgreement(null, { ...h, location_id: loc, lines: lines.filter((l) => l.variant_id).map((l) => ({ variant_id: l.variant_id, price: Number(l.price), list_price: Number(l.list_price || 0), max_units: l.max_units === "" ? null : Number(l.max_units) })) })
    );
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <Panel title="New fleet agreement" onHide={() => !busy && onHide()} width={900} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Save draft</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Fleet customer" required>
          <AppDropdown id="fa-acct" value={h.fleet_account_id} options={opts["fleet-accounts"] || []} onChange={(e) => setH({ ...h, fleet_account_id: e.value })} />
        </Field>
        <Field label="Location">
          <AppDropdown id="fa-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="From" required>
          <input type="date" className="form-control" style={inputStyle} value={h.valid_from} onChange={(e) => setH({ ...h, valid_from: e.target.value })} />
        </Field>
        <Field label="To" required>
          <input type="date" className="form-control" style={inputStyle} value={h.valid_to} onChange={(e) => setH({ ...h, valid_to: e.target.value })} />
        </Field>
      </div>
      <h6>Prices (before VAT)</h6>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ flex: 1, minWidth: 240 }}>
            <AppDropdown id={`fa-v-${i}`} value={l.variant_id} filter options={opts.variants || []} onChange={(e) => set(i, { variant_id: e.value })} placeholder="Variant" />
          </div>
          <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 130 }} placeholder="Agreed price" aria-label="Agreed price" value={l.price} onChange={(e) => set(i, { price: e.target.value })} />
          <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="List price" aria-label="List price" value={l.list_price} onChange={(e) => set(i, { list_price: e.target.value })} />
          <input type="number" min={1} className="form-control" style={{ ...inputStyle, width: 110 }} placeholder="Max units" aria-label="Max units" value={l.max_units} onChange={(e) => set(i, { max_units: e.target.value })} />
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, { variant_id: null, price: "", list_price: "", max_units: "" }])}>
        Add variant
      </button>
    </Panel>
  );
}

function OrderFromAgreement({ agreement, onHide, onDone }) {
  const team = useDmsOptions(["salespersons", "order-types"]);
  const [h, setH] = useState({ salesperson_employee_id: null, order_type_id: null, customer_po: "" });
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const res = await run(() =>
      dmsService.fleetAgreementAction(agreement.id, "orders", {
        ...h,
        salesperson_employee_id: h.salesperson_employee_id || undefined,
        order_type_id: h.order_type_id || undefined,
        lines: agreement.lines.map((l, i) => ({ variant_id: l.variant_id, quantity: Number(qty[i] || 0) })).filter((l) => l.quantity > 0),
      })
    );
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <Panel title={`Order under ${agreement.agreement_number}`} onHide={() => !busy && onHide()} width={720} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Create order</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Sales consultant">
          <AppDropdown id="fo-sp" value={h.salesperson_employee_id} options={(team.salespersons || []).map((s) => ({ label: s.row.employee_name, value: s.row.employee_id }))} onChange={(e) => setH({ ...h, salesperson_employee_id: e.value })} placeholder="Me" />
        </Field>
        <Field label="Order type">
          <AppDropdown id="fo-ot" value={h.order_type_id} options={[{ label: "Fleet (default)", value: null }, ...(team["order-types"] || []).filter((o) => o.row.category === "VEHICLE")]} onChange={(e) => setH({ ...h, order_type_id: e.value })} />
        </Field>
        <Field label="Customer PO">
          <input className="form-control" style={inputStyle} value={h.customer_po} onChange={(e) => setH({ ...h, customer_po: e.target.value })} />
        </Field>
      </div>
      {agreement.lines.map((l, i) => (
        <div key={l.variant_code} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14 }}>
          <div style={{ flex: 1 }}>
            {l.model_name} {l.variant_name} · {fmtMoney(l.price)}
            <span style={hint}> · {l.units_ordered} ordered{l.max_units ? ` of ${l.max_units}` : ""}</span>
          </div>
          <input type="number" min={0} className="form-control" style={{ ...inputStyle, width: 100 }} aria-label={`Vehicles of ${l.variant_name}`} value={qty[i] ?? ""} onChange={(e) => setQty({ ...qty, [i]: e.target.value })} />
        </div>
      ))}
    </Panel>
  );
}

function AgreementView({ id, onHide, onChanged }) {
  const { data: a, loading, error, reload } = useLoad(() => dmsService.getFleetAgreement(id), [id]);
  const [ordering, setOrdering] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={a ? `Fleet agreement ${a.agreement_number}` : "Fleet agreement"} onHide={onHide} width={900}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {a ? (
          <>
            <DocHead title="" number={a.account ? a.account.name : ""} status={a.status} sub={`${formatDate(a.valid_from)} – ${formatDate(a.valid_to)} · ${a.currency}`}>
              {a.status === "DRAFT" ? <PrimaryButton onClick={() => run(() => dmsService.fleetAgreementAction(id, "submit"), refresh)}>Submit for approval</PrimaryButton> : null}
              {a.status === "ACTIVE" ? <PrimaryButton onClick={() => setOrdering(true)}>New fleet order</PrimaryButton> : null}
            </DocHead>
            <Section title="Prices">
              <DataTable
                rows={a.lines}
                rowKey={(l) => l.variant_code}
                columns={[
                  { label: "Variant", render: (l) => `${l.model_name} ${l.variant_name}` },
                  { label: "List", num: true, render: (l) => fmtMoney(l.list_price) },
                  { label: "Agreed", num: true, render: (l) => fmtMoney(l.price) },
                  { label: "Ordered", num: true, render: (l) => `${l.units_ordered}${l.max_units ? ` / ${l.max_units}` : ""}` },
                ]}
              />
            </Section>
            <Section title="Orders">
              <DataTable
                rows={a.orders}
                rowKey={(o) => o.so_number}
                empty={<div style={hint}>No orders yet.</div>}
                columns={[
                  { label: "Order", render: (o) => <b>{o.so_number}</b> },
                  { label: "Date", render: (o) => formatDate(o.order_date) },
                  { label: "Total", num: true, render: (o) => fmtMoney(o.total) },
                  { label: "Status", render: (o) => <DmsBadge status={o.status} /> },
                ]}
              />
              <div style={{ ...hint, marginTop: 6 }}>Open the order under Sales Orders to reserve, deliver in batches and invoice the deliveries together.</div>
            </Section>
            <Section title="Action history">
              <ActionHistory actions={a.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      {ordering && a ? <OrderFromAgreement agreement={a} onHide={() => setOrdering(false)} onDone={refresh} /> : null}
    </Panel>
  );
}

export default function FleetSales() {
  const { data, loading, error, reload } = useLoad(() => dmsService.listFleetAgreements(), []);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Fleet Sales" subtitle="Corporate agreements and fleet orders" actions={<PrimaryButton onClick={() => setCreating(true)}>New agreement</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No fleet agreements" subtitle="Add fleet customers in Dealership Setup → Fleet & insurers first." />}
          columns={[
            { label: "Agreement", render: (r) => <b>{r.agreement_number}</b> },
            { label: "Customer", render: (r) => r.account_name },
            { label: "Valid", render: (r) => `${formatDate(r.valid_from)} – ${formatDate(r.valid_to)}` },
            { label: "Variants", num: true, render: (r) => Number(r.variants) },
            { label: "Vehicles ordered", num: true, render: (r) => Number(r.units_ordered) },
            { label: "Status", render: (r) => <DmsBadge status={r.status === "ACTIVE" ? "APPROVED" : r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <AgreementView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? <AgreementEditor onHide={() => setCreating(false)} onDone={(id) => {
            reload();
            setOpen(id);
          }} /> : null}
    </div>
  );
}
